import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { companyInfo } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Error submitting contact form:', error);
      }
      toast.error(error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            <span className="text-orange-600">Contactez</span>-nous
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une question ? Un projet ? N'hésitez pas à nous contacter pour un devis gratuit
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 max-w-6xl mx-auto">
          {/* Contact Information — compact, 2/5 width */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-2xl p-8 text-white h-full">
              <h3 className="text-2xl font-bold mb-6">Nos coordonnées</h3>

              <div className="space-y-5">
                <a
                  href={`tel:${companyInfo.contact.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-4 hover:bg-white/10 -mx-2 px-2 py-1.5 rounded-lg transition-colors"
                  data-testid="contact-phone-link"
                >
                  <div className="bg-white/20 p-3 rounded-lg flex-shrink-0">
                    <Phone size={22} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-orange-100">Téléphone</p>
                    <p className="font-semibold">{companyInfo.contact.phone}</p>
                  </div>
                </a>

                <a
                  href={`mailto:${companyInfo.contact.email}`}
                  className="flex items-center gap-4 hover:bg-white/10 -mx-2 px-2 py-1.5 rounded-lg transition-colors"
                  data-testid="contact-email-link"
                >
                  <div className="bg-white/20 p-3 rounded-lg flex-shrink-0">
                    <Mail size={22} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-orange-100">Email</p>
                    <p className="font-semibold break-all">{companyInfo.contact.email}</p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-lg flex-shrink-0">
                    <MapPin size={22} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-orange-100">Zone d'intervention</p>
                    <p className="font-semibold">{companyInfo.contact.address}</p>
                    <p className="text-orange-100 text-sm">Toute l'Île-de-France</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form — 3/5 width */}
          <div className="lg:col-span-3">
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-black mb-6">Demande de devis gratuit</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="06 12 34 56 78"
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">Sujet</Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Objet de votre demande"
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Décrivez votre projet ou votre demande..."
                    rows={4}
                    className="mt-1.5"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white py-5"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      Envoyer ma demande
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
